import { useEffect } from 'react';

import { Row, Col, Typography, Select } from 'antd';

import { useParams } from 'react-router-dom';

import classnames from 'classnames';

import {
  CustomNotification,
  Form,
  SectionWrapper,
  WithLoading,
} from 'common/components';

import {
  HighlightContent,
  PrimaryFieldSnapshot,
  RetailerSnapshot,
} from '../qa-prod-spec/snapshot/RetailerSnapshot';

import QaSpecPetFoodGeometries from './QaSpecPetFoodGeometries';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';

import {
  EDIT_SECTION,
  EditTabSectionWrapper,
} from 'pages/qa-spec/components/edit-tab-checker';

import EditContainer from '../container/EditContainer';

import {
  useGetQaPetFood,
  useGetQaSpecPetFoodFormDefine,
  useGetSnapshotQaPetFood,
  useSaveQaPetFood,
  useCheckPetFoodQasRequiredField,
} from './hooks';

import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';

import './QaPetFood.less';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { COMMENT_FIELDS } from 'static/QaSpecFields';
const QaPetFood = () => {
  const [formInstance] = Form.useForm();

  const { id: productId } = useParams();

  const { data: qaPetFood, isLoading } = useGetQaPetFood(productId);
  const { data: qaPetFoodSnapshot } = useGetSnapshotQaPetFood(productId);

  const petFoodMutation = useSaveQaPetFood(productId);

  useEffect(() => {
    if (qaPetFood) {
      formInstance.setFieldsValue({ ...qaPetFood });
    }
  }, [formInstance, qaPetFood]);

  const handleSave = async (toggleEditMode) => {
    try {

      //const formValues = await formInstance.validateFields();
      const formValues = formInstance.getFieldsValue(true);
      const geometries = formValues?.geometries ?? [];
     
      const hasInvalidRow = geometries.some(row => {
        // row itself is null/undefined
        if (!row) {
          return true;
        }

        return Object.values(row).every(
          value => value === null || value === undefined || value === ''
        );
      });

      if (hasInvalidRow) {
        CustomNotification.error(
          'Please fill at least one field in each Geometries row'
        );
        return;
      }

      const params = {
        productId,
        ...formValues,
      };

      petFoodMutation.mutate(params, { onSuccess: () => toggleEditMode() });
    } catch (error) {
      if (error?.errorFields?.length) {
        CustomNotification.error(' Please resolve errors in the Pet Food tab');
        return;
      }

      CustomNotification.error('Failed to save data');
    }
  };

  const formDefine = useGetQaSpecPetFoodFormDefine({ productId });

  const handleCancel = () => {
    formInstance.setFieldsValue({ ...qaPetFood });
  };

  return (
    <Form form={formInstance} className='qa-pet-food'>
      <WithLoading loading={isLoading}>
        <EditContainer
          type={EDIT_SECTION.QA_PET_FOOD}
          productId={productId}
          saveLoading={petFoodMutation.isLoading}
          handleSave={handleSave}
          handleCancel={handleCancel}
          float
        >
          {(toggleEditMode) => {
            const disabled = !toggleEditMode;

            return (
              <EditTabSectionWrapper.CheckerWrapper
                isEdit={toggleEditMode}
                type={EDIT_SECTION.QA_PET_FOOD}
              >
                <Row
                  gutter={[8, 8]}
                  wrap={false}
                  className={classnames('qa-pet-food__input', {
                    'qa-pet-food__input--edit': toggleEditMode,
                  })}
                >
                  <Col
                    span={10}
                    style={{ display: 'flex', flexDirection: 'column' }}
                  >
                    <SectionWrapper title='Feeding'>
                      <PetFoodFeeding
                        productId={productId}
                        disabled={disabled}
                        qaPetFood={qaPetFood}
                        qaPetFoodSnapshot={qaPetFoodSnapshot}
                      />
                    </SectionWrapper>
                    <SectionWrapper
                      title='Additional Information'
                      style={{ paddingTop: 30, flex: '1' }}
                      className='qa-pet-food__additional-information'
                    >
                      <PetFoodAdditionalInformation
                        productId={productId}
                        disabled={disabled}
                        qaPetFood={qaPetFood}
                        qaPetFoodSnapshot={qaPetFoodSnapshot}
                      />
                    </SectionWrapper>
                  </Col>
                  <Col flex={1}>
                    <SectionWrapper title='Details'>
                      <PetFoodDetails
                        productId={productId}
                        disabled={disabled}
                        qaPetFood={qaPetFood}
                        qaPetFoodSnapshot={qaPetFoodSnapshot}
                      />
                    </SectionWrapper>
                  </Col>
                </Row>
                <Row gutter={[8, 0]} style={{ marginTop: 8 }}>
                  <Col span={24}>
                    <QaSpecPetFoodGeometries
                      productId={productId}
                      isEdit={toggleEditMode}
                      formDefine={formDefine.geometries}
                      geometries={qaPetFood?.geometries ?? []}
                      geometriesSnapshot={qaPetFoodSnapshot?.geometries ?? []}
                    />
                  </Col>
                </Row>
              </EditTabSectionWrapper.CheckerWrapper>
            );
          }}
        </EditContainer>
      </WithLoading>
    </Form>
  );
};

const PetFoodFeeding = ({
  productId,
  disabled,
  qaPetFood,
  qaPetFoodSnapshot,
}) => {
  const { getRequiredRule } = useCheckPetFoodQasRequiredField({ productId });

  return (
    <Row>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.dailyFeedingRecommendations}
          snapshotValue={qaPetFoodSnapshot?.dailyFeedingRecommendations}
          highlightContent={
            <HighlightContent
              label='Daily Feeding Recommendations:'
              value={qaPetFoodSnapshot?.dailyFeedingRecommendations}
            />
          }
        >
          <Form.Item
            name='dailyFeedingRecommendations'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Daily Feeding Recommendations'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.dailyFeedingRecommendations'>
                  <LabelForm label='Daily Feeding Recommendations:' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'dailyFeedingRecommendations',
                displayName: 'Daily Feeding Recommendations:',
              }),
            ]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <PrimaryFieldSnapshot readOnly={disabled} />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.feedingInstructions}
          snapshotValue={qaPetFoodSnapshot?.feedingInstructions}
          highlightContent={
            <HighlightContent
              label='Feeding Instructions:'
              value={qaPetFoodSnapshot?.feedingInstructions}
            />
          }
        >
          <Form.Item
            name='feedingInstructions'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Feeding Instructions'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.feedingInstructions'>
                  <LabelForm label='Feeding Instructions:' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'feedingInstructions',
                displayName: 'Feeding Instructions',
              }),
            ]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <PrimaryFieldSnapshot readOnly={disabled} />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.feedingGuidelines}
          snapshotValue={qaPetFoodSnapshot?.feedingGuidelines}
          highlightContent={
            <HighlightContent
              label='Feeding Guidelines:'
              value={qaPetFoodSnapshot?.feedingGuidelines}
            />
          }
        >
          <Form.Item
            name='feedingGuidelines'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Feeding Guidelines'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.feedingGuidelines'>
                  <LabelForm label='Feeding Guidelines:' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'feedingGuidelines',
                displayName: 'Feeding Guidelines',
              }),
            ]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <PrimaryFieldSnapshot readOnly={disabled} />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.appetiteControlCareProgram}
          snapshotValue={qaPetFoodSnapshot?.appetiteControlCareProgram}
          highlightContent={
            <HighlightContent
              label='Appetite Control Care Program:'
              value={qaPetFoodSnapshot?.appetiteControlCareProgram}
            />
          }
        >
          <Form.Item
            name='appetiteControlCareProgram'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Appetite Control Care Program'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.appetiteControlCareProgram'>
                  <LabelForm label='Appetite Control Care Program:' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'appetiteControlCareProgram',
                displayName: 'Appetite Control Care Program:',
              }),
            ]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <PrimaryFieldSnapshot readOnly={disabled} />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.veterinarianStatement}
          snapshotValue={qaPetFoodSnapshot?.veterinarianStatement}
          highlightContent={
            <HighlightContent
              label='Use Only As Directed by your Veterinarian Statement:'
              value={qaPetFoodSnapshot?.veterinarianStatement}
            />
          }
        >
          <Form.Item
            name='veterinarianStatement'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Use Only As Directed by your Veterinarian Statement'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.veterinarianStatement'>
                  <LabelForm label='Use Only As Directed by your Veterinarian Statement:' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'veterinarianStatement',
                displayName: 'Veterinarian Statement',
              }),
            ]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <PrimaryFieldSnapshot readOnly={disabled} />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
    </Row>
  );
};
const PetFoodAdditionalInformation = ({
  productId,
  disabled,
  qaPetFood,
  qaPetFoodSnapshot,
}) => {
  const { getRequiredRule } = useCheckPetFoodQasRequiredField({ productId });

  return (
    <>
      <QaPetFoodSnapshot
        supplierModifiedValue={qaPetFood?.humanGrade}
        snapshotValue={qaPetFoodSnapshot?.humanGrade}
        highlightContent={
          <HighlightContent
            label='Human Grade:'
            value={qaPetFoodSnapshot?.humanGrade}
          />
        }
      >
        <Form.Item
          name='humanGrade'
          label={
            <TraceableCommentContainer
              qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
              qaSpecFieldName='Human Grade'
            >
              <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.humanGrade'>
                <LabelForm label='Human Grade' />
              </QaSpecHelpContentLabel>
            </TraceableCommentContainer>
          }
          rules={[
            getRequiredRule({
              fieldName: 'humanGrade',
              displayName: 'Human Grade',
            }),
          ]}
          labelCol={{ span: 12 }}
        >
          <PrimaryFieldSnapshot variant='select' disabled={disabled}>
            {['Yes', 'No'].map((option) => (
              <Select.Option value={option}>{option}</Select.Option>
            ))}
          </PrimaryFieldSnapshot>
        </Form.Item>
      </QaPetFoodSnapshot>
      <QaPetFoodSnapshot
        supplierModifiedValue={qaPetFood?.containsPalmOil}
        snapshotValue={qaPetFoodSnapshot?.containsPalmOil}
        highlightContent={
          <HighlightContent
            label='Contains Palm Oil'
            value={qaPetFoodSnapshot?.containsPalmOil}
          />
        }
      >
        <Form.Item
          name='containsPalmOil'
          label={
            <TraceableCommentContainer
              qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
              qaSpecFieldName='Contains Palm Oil'
            >
              <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.containsPalmOil'>
                <LabelForm label='Contains Palm Oil' />
              </QaSpecHelpContentLabel>
            </TraceableCommentContainer>
          }
          rules={[
            getRequiredRule({
              fieldName: 'containsPalmOil',
              displayName: 'Contains Palm Oil',
            }),
          ]}
          labelCol={{ span: 12 }}
        >
          <PrimaryFieldSnapshot variant='select' disabled={disabled}>
            {['Yes', 'No'].map((option) => (
              <Select.Option value={option}>{option}</Select.Option>
            ))}
          </PrimaryFieldSnapshot>
        </Form.Item>
      </QaPetFoodSnapshot>

      <QaPetFoodSnapshot
        supplierModifiedValue={qaPetFood?.ingredientsOutsideUs}
        snapshotValue={qaPetFoodSnapshot?.ingredientsOutsideUs}
        highlightContent={
          <HighlightContent
            label='Ingredients in product from outside the United States'
            value={qaPetFoodSnapshot?.ingredientsOutsideUs}
          />
        }
      >
        <Form.Item
          name='ingredientsOutsideUs'
          label={
            <TraceableCommentContainer
              qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
              qaSpecFieldName='Ingredients in product from outside the United States'
            >
              <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.ingredientsOutsideUs'>
                <LabelForm label='Ingredients in product from outside the United States' />
              </QaSpecHelpContentLabel>
            </TraceableCommentContainer>
          }
          rules={[
            getRequiredRule({
              fieldName: 'ingredientsOutsideUs',
              displayName:
                'Ingredients in product from outside the United States',
            }),
          ]}
          labelCol={{ span: 12 }}
        >
          <PrimaryFieldSnapshot variant='select' disabled={disabled}>
            {['Yes', 'No'].map((option) => (
              <Select.Option value={option}>{option}</Select.Option>
            ))}
          </PrimaryFieldSnapshot>
        </Form.Item>
      </QaPetFoodSnapshot>
    </>
  );
};
const PetFoodDetails = ({
  productId,
  disabled,
  qaPetFood,
  qaPetFoodSnapshot,
}) => {
  const { getRequiredRule } = useCheckPetFoodQasRequiredField({ productId });

  return (
    <Row gutter={[0, 4]}>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.benefits}
          snapshotValue={qaPetFoodSnapshot?.benefits}
          highlightContent={
            <HighlightContent
              label='Benefits:'
              value={qaPetFoodSnapshot?.benefits}
            />
          }
        >
          <Form.Item
            name='benefits'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Benefits'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.benefits'>
                  <LabelForm label='Benefits' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'benefits',
                displayName: 'Benefits',
              }),
            ]}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <PrimaryFieldSnapshot
              variant='textarea'
              autoSize={{ minRows: 4, maxRows: 5 }}
              readOnly={disabled}
            />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.warnings}
          snapshotValue={qaPetFoodSnapshot?.warnings}
          highlightContent={
            <HighlightContent
              label='Warnings:'
              value={qaPetFoodSnapshot?.warnings}
            />
          }
        >
          <Form.Item
            name='warnings'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Warnings'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.warnings'>
                  <LabelForm label='Warnings' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'warnings',
                displayName: 'Warnings',
              }),
            ]}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <PrimaryFieldSnapshot
              variant='textarea'
              autoSize={{ minRows: 4, maxRows: 5 }}
              readOnly={disabled}
            />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.nutritionalAdequacyStatement}
          snapshotValue={qaPetFoodSnapshot?.nutritionalAdequacyStatement}
          highlightContent={
            <HighlightContent
              label='Nutritional Adequacy Statement:'
              value={qaPetFoodSnapshot?.nutritionalAdequacyStatement}
            />
          }
        >
          <Form.Item
            name='nutritionalAdequacyStatement'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Nutritional Adequacy Statement'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.nutritionalAdequacyStatement'>
                  <LabelForm label='Nutritional Adequacy Statement' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'nutritionalAdequacyStatement',
                displayName: 'Nutritional Adequacy Statement',
              }),
            ]}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <PrimaryFieldSnapshot
              variant='textarea'
              autoSize={{ minRows: 4, maxRows: 5 }}
              readOnly={disabled}
            />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.productClaims}
          snapshotValue={qaPetFoodSnapshot?.productClaims}
          highlightContent={
            <HighlightContent
              label='Product Claims:'
              value={qaPetFoodSnapshot?.productClaims}
            />
          }
        >
          <Form.Item
            name='productClaims'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Product Claims'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.productClaims'>
                  <LabelForm label='Product Claims' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'productClaims',
                displayName: 'Product Claims',
              }),
            ]}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <PrimaryFieldSnapshot
              variant='textarea'
              autoSize={{ minRows: 4, maxRows: 5 }}
              readOnly={disabled}
            />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.usageInstructions}
          snapshotValue={qaPetFoodSnapshot?.usageInstructions}
          highlightContent={
            <HighlightContent
              label='Usage Instructions:'
              value={qaPetFoodSnapshot?.usageInstructions}
            />
          }
        >
          <Form.Item
            name='usageInstructions'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Usage Instructions'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.usageInstructions'>
                  <LabelForm label='Usage Instructions' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'usageInstructions',
                displayName: 'Usage Instructions',
              }),
            ]}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <PrimaryFieldSnapshot
              variant='textarea'
              autoSize={{ minRows: 4, maxRows: 5 }}
              readOnly={disabled}
            />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
      <Col span={24}>
        <QaPetFoodSnapshot
          supplierModifiedValue={qaPetFood?.proteinContentClaim}
          snapshotValue={qaPetFoodSnapshot?.proteinContentClaim}
          highlightContent={
            <HighlightContent
              label='Protein Content Claim:'
              value={qaPetFoodSnapshot?.proteinContentClaim}
            />
          }
        >
          <Form.Item
            name='proteinContentClaim'
            label={
              <TraceableCommentContainer
                qaSpecName={COMMENT_FIELDS.petFood.qaSpecName}
                qaSpecFieldName='Protein Content Claim'
              >
                <QaSpecHelpContentLabel fieldPath='qaSpecification.petFood.proteinContentClaim'>
                  <LabelForm label='Protein Content Claim' />
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            }
            rules={[
              getRequiredRule({
                fieldName: 'proteinContentClaim',
                displayName: 'Protein Content Claim',
              }),
            ]}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <PrimaryFieldSnapshot
              variant='textarea'
              autoSize={{ minRows: 4, maxRows: 5 }}
              readOnly={disabled}
            />
          </Form.Item>
        </QaPetFoodSnapshot>
      </Col>
    </Row>
  );
};

const LabelForm = ({ label }) => {
  return (
    <Typography.Text strong ellipsis={{ tooltip: label }}>
      {label}
    </Typography.Text>
  );
};

export const QaPetFoodSnapshot = (props) => {
  const { id: productId } = useParams();
  const { isSuccess, data } = useGetSnapshotQaPetFood(productId);

  const isToggleSnapshot = useCheckSnapshotForRetailer();

  if (isSuccess && isToggleSnapshot && data) {
    return <RetailerSnapshot {...props} />;
  }

  return <>{props.children}</>;
};

export default QaPetFood;
